import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { me, BackendUnavailableError } from "../authService";
import { getStatuses, getCarriers } from "../../admin/adminService";

const API_URL = process.env.REACT_APP_API_URL || '';

// Пауза між повторами /auth/me, поки бекенд недоступний (стартує/перезапускається).
const BACKEND_RETRY_MS = 3000;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [directions, setDirections] = useState([]);
  const [organizationRoles, setOrganizationRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [apps, setApps] = useState([]);
  const [statusCatalog, setStatusCatalog] = useState({});
  const [carrierCatalog, setCarrierCatalog] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [backendUnavailable, setBackendUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchUser = async () => {
      setIsLoading(true);
      try {
        // Бекенд недоступний — це не «немає сесії»: чекаємо й повторюємо, користувача
        // на вхід не скидаємо (інакше після старту бекенда довелося б вручну перезавантажувати).
        let data;
        for (;;) {
          try {
            data = await me();
            break;
          } catch (err) {
            if (!(err instanceof BackendUnavailableError)) throw err;
            if (cancelled) return;
            setBackendUnavailable(true);
            await new Promise((resolve) => setTimeout(resolve, BACKEND_RETRY_MS));
            if (cancelled) return;
          }
        }
        setBackendUnavailable(false);
        if (cancelled) return;
        if (data?.authenticated) {
          // Если пользователь заблокирован — сбрасываем состояние и редиректим на logout
          if (data.profile?.isActive === false) {
            clearSession();
            window.location.href = '/auth/logout';
            return;
          }

          // Нормализуем относительный путь аватара — добавляем API_URL
          if (data.profile?.avatar?.startsWith('/users/')) {
            data.profile.avatar = `${API_URL}${data.profile.avatar}`;
          }
          setUser(data.profile);
          setDirections(data.directions || []);
          setOrganizationRoles(data.organizationRoles || []);
          setPermissions(data.permissions || []);
          setApps(data.apps || []);

          // Desktop (Tauri/WebView2): подтверждённая сессия — триггер бэкапа кук на
          // диск (persist_login_cookies в desktop/src-tauri/src/lib.rs). Срабатывает
          // и на "только что залогинился", и на обычный reload уже залогиненной
          // сессии — отдельного сигнала "свежий логин" без правок бэкенда нет, но
          // вызов дешёвый и идемпотентный. Fire-and-forget с .catch() — если бы
          // ошибка утекла в общий try/catch этого эффекта, неудачный бэкап кук
          // ошибочно сбросил бы user в null.
          if (window.__TAURI__) {
            window.__TAURI__.core.invoke('persist_login_cookies').catch(() => {});
          }
        } else {
          // 401 — бекенд уже destroySession + clearCookie
          setUser(null);
          setDirections([]);
          setOrganizationRoles([]);
          setPermissions([]);
          setApps([]);
        }
      } catch (err) {
        console.error("AuthContext: error fetching user", err);
        if (!cancelled) {
          setUser(null);
          setDirections([]);
          setOrganizationRoles([]);
          setPermissions([]);
          setApps([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchUser();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Довідник статусів потрібен лише авторизованим користувачам (використовується
    // тільки в захищених модулях); /api/statuses вимагає auth, а спільний api()-хелпер
    // adminService.js редіректить на логін при 401 — без цього гварду гість ніколи
    // не побачив би гостьову Home.jsx.
    if (!user) return;
    let cancelled = false;
    getStatuses()
      .then((list) => {
        if (cancelled) return;
        const map = {};
        for (const s of list) {
          map[s.key] = { label: s.label, color: s.color, bgColor: s.bgColor, icon: s.icon };
        }
        setStatusCatalog(map);
      })
      .catch((err) => console.error("AuthContext: error fetching statuses", err));
    return () => { cancelled = true; };
  }, [Boolean(user)]);

  const refreshCarrierCatalog = useCallback(async () => {
    const list = await getCarriers();
    const map = {};
    for (const c of list) {
      map[c.id] = {
        label: c.label,
        hasApiIntegration: c.hasApiIntegration,
        apiProvider: c.apiProvider,
        icon: c.icon,
        // versioned by updatedAt — інакше Cache-Control: max-age=86400 на
        // /carriers/:id/logo продовжує віддавати стару картинку з браузерного
        // кешу після заміни логотипу, доки URL лишається незмінним
        logo: c.logo ? `${API_URL}/carriers/${c.id}/logo?v=${encodeURIComponent(c.updatedAt)}` : null,
        isDefault: c.isDefault,
        supportsDeliveryDefaults: c.supportsDeliveryDefaults,
        isGeneric: c.isGeneric,
        sortOrder: c.sortOrder,
      };
    }
    setCarrierCatalog(map);
  }, []);

  useEffect(() => {
    // Довідник перевізників — той самий гвард, що й для статусів (лише авторизовані).
    if (!user) return;
    let cancelled = false;
    refreshCarrierCatalog().catch((err) => {
      if (!cancelled) console.error("AuthContext: error fetching carriers", err);
    });
    return () => { cancelled = true; };
  }, [Boolean(user), refreshCarrierCatalog]);

  const clearSession = useCallback(() => {
    setUser(null);
    setDirections([]);
    setOrganizationRoles([]);
    setPermissions([]);
    setApps([]);
    setStatusCatalog({});
    setCarrierCatalog({});
  }, []);

  // Хелпер: есть ли разрешение у пользователя
  const userCan = useCallback((appSlug, permission) => {
    // Глобальный ADMIN имеет все права
    if (user?.globalRole === 'ADMIN') return true;
    return permissions.some(
      (p) => p.appSlug === appSlug && p.key === permission
    );
  }, [permissions, user]);

  // Хелпер: есть ли разрешение в конкретном подразделении
  const userCanInDept = useCallback((appSlug, permission, departmentId) => {
    // Глобальный ADMIN имеет все права
    if (user?.globalRole === 'ADMIN') return true;
    return permissions.some(
      (p) =>
        p.appSlug === appSlug &&
        p.key === permission &&
        (!p.departmentId || p.departmentId === departmentId)
    );
  }, [permissions, user]);

  // Хелпер: роль пользователя в приложении
  const getAppRole = useCallback((appSlug) => {
    const app = apps.find((a) => a.slug === appSlug);
    return app?.role || null;
  }, [apps]);

  return (
    <AuthContext.Provider
      value={{
        user, setUser,
        directions,
        organizationRoles,
        permissions,
        apps,
        statusCatalog,
        carrierCatalog,
        refreshCarrierCatalog,
        userCan,
        userCanInDept,
        getAppRole,
        clearSession,
        isLoading,
        backendUnavailable,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
